import { useState, useCallback, useMemo } from 'react';
import getIn from 'immutable-less/transmute/getIn';
import { useGetDataSources, useLazyGetDataSources } from 'automation-ui-formula-editor/data-sources/hooks/useGetDataSources';
export const useGetDataProperties = fetchProperties => {
  const prefixReqs = fetchProperties.map(fetchPrefix => {
    return {
      prefix: fetchPrefix.prefix,
      options: fetchPrefix.options
    };
  });
  const {
    data: queryData,
    loading,
    error
  } = useGetDataSources(prefixReqs);
  const data = useMemo(() => {
    if (loading || !queryData || !fetchProperties.length) return;
    const dataToReturn = new Map();
    fetchProperties.forEach(reqObj => {
      var _dataToReturn$get;
      const prefix = reqObj.prefix;
      const property = reqObj.property;
      const dataSourceData = queryData.get(prefix);
      const propertyData = getIn(['dataSourceProperties', property], dataSourceData);
      let dataSourceProperties = ((_dataToReturn$get = dataToReturn.get(prefix)) === null || _dataToReturn$get === void 0 ? void 0 : _dataToReturn$get.dataSourceProperties) || {};
      if (propertyData) {
        dataSourceProperties = Object.assign({}, dataSourceProperties, {
          [property]: propertyData
        });
      }
      dataToReturn.set(prefix, Object.assign({}, dataSourceData, {
        dataSourceProperties
      }));
    });
    return dataToReturn;
  }, [queryData, loading, fetchProperties]);
  return {
    data,
    loading,
    error
  };
};
export const useLazyGetDataProperties = () => {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const {
    getDataSourcesWithAPromise,
    error
  } = useLazyGetDataSources();
  const getDataProperties = useCallback(propertyReqs => {
    const prefixReqs = propertyReqs.map(fetchProperty => {
      return {
        prefix: fetchProperty.prefix,
        options: fetchProperty.options
      };
    });
    getDataSourcesWithAPromise(prefixReqs).then(queryData => {
      const gatheredData = propertyReqs.reduce((acc, reqObj) => {
        const prefix = reqObj.prefix;
        const property = reqObj.property;
        const dataSourceData = queryData.get(prefix);
        const propertyData = getIn(['dataSourceProperties', property], dataSourceData);
        let dataSourceProperties = {};
        if (acc.has(prefix)) {
          dataSourceProperties = acc.get(prefix).dataSourceProperties;
        }
        if (propertyData) {
          dataSourceProperties[property] = propertyData;
        }
        acc.set(prefix, Object.assign({}, dataSourceData, {
          dataSourceProperties
        }));
        return acc;
      }, new Map());
      setData(gatheredData);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, [getDataSourcesWithAPromise]);
  const getDataPropertiesByTokens = useCallback(tokens => {
    const propertyReqs = tokens.map(token => {
      if (!token) return null;
      const splitToken = token.split('.');
      const property = splitToken.pop() || '';
      const prefix = splitToken.join('.') || '';
      if (!prefix || !property) {
        return null;
      }
      return {
        prefix,
        property
      };
    }).filter(result => !!result);
    getDataProperties(propertyReqs);
  }, [getDataProperties]);

  // Specific use case methods that return a promise //
  const getDataPropertiesWithAPromise = useCallback(propertyReqs => {
    const prefixReqs = propertyReqs.map(fetchProperty => {
      return {
        prefix: fetchProperty.prefix,
        options: fetchProperty.options
      };
    });
    return getDataSourcesWithAPromise(prefixReqs).then(queryData => {
      const gatheredData = propertyReqs.reduce((acc, reqObj) => {
        const prefix = reqObj.prefix;
        const property = reqObj.property;
        const dataSourceData = queryData.get(prefix);
        const propertyData = getIn(['dataSourceProperties', property], dataSourceData);
        let dataSourceProperties = {};
        if (acc.has(prefix)) {
          dataSourceProperties = acc.get(prefix).dataSourceProperties;
        }
        if (propertyData) {
          dataSourceProperties[property] = propertyData;
        }
        acc.set(prefix, Object.assign({}, dataSourceData, {
          dataSourceProperties
        }));
        return acc;
      }, new Map());
      return gatheredData;
    });
  }, [getDataSourcesWithAPromise]);
  const getDataPropertiesByTokensWithAPromise = useCallback(tokens => {
    const propertyReqs = tokens.map(token => {
      if (!token) return null;
      const splitToken = token.split('.');
      const property = splitToken.pop() || '';
      const prefix = splitToken.join('.') || '';
      if (!prefix || !property) {
        return null;
      }
      return {
        prefix,
        property
      };
    }).filter(result => !!result);
    return getDataPropertiesWithAPromise(propertyReqs);
  }, [getDataPropertiesWithAPromise]);
  return {
    getDataProperties,
    getDataPropertiesByTokens,
    getDataPropertiesWithAPromise,
    getDataPropertiesByTokensWithAPromise,
    data,
    loading,
    error
  };
};